import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Search,
  Visibility,
  Person as PersonIcon,
  Add,
  Refresh,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';

const UserList = () => {
  const { actions } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // The actual search being performed
  const [exporting, setExporting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [nextCursor, setNextCursor] = useState('');
  const isRequestInProgressRef = useRef(false);
  const cursorsRef = useRef({}); // Store cursors for each page

  const fetchUsers = useCallback(async (page = 1, search = '', cursor = '', shouldScrollToTop = false) => {
    // Prevent multiple simultaneous requests
    if (isRequestInProgressRef.current) {
      console.log('Request already in progress, skipping...');
      return;
    }

    try {
      isRequestInProgressRef.current = true;
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search }),
        ...(cursor && { cursor })
      });
      
      console.log('Fetching users with params:', params.toString());
      const response = await api.get(`/users?${params}`);
      
      if (response.data.success) {
        setUsers(response.data.users || []);
        setCurrentPage(response.data.currentPage || page);
        setHasNextPage(response.data.hasNextPage || false);
        setHasPrevPage(response.data.hasPrevPage || false);
        
        // Store cursor for next page
        if (response.data.nextCursor) {
          setNextCursor(response.data.nextCursor);
          cursorsRef.current[page + 1] = response.data.nextCursor;
        }
        
        console.log(`Loaded ${response.data.users?.length || 0} users for page ${page}`);
        
        // Scroll to top after data is loaded if requested
        if (shouldScrollToTop) {
          setTimeout(() => {
            try {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
            }
          }, 100);
        }
      } else {
        // Clear users on API failure but don't show repeated errors
        setUsers([]);
        setCurrentPage(1);
        setHasNextPage(false);
        setHasPrevPage(false);
        console.error('API returned failure:', response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // Clear users on error
      setUsers([]);
      setCurrentPage(1);
      setHasNextPage(false);
      setHasPrevPage(false);
      
      // Show error message (but useEffect won't trigger again due to stable refs)
      actions.showSnackbar(
        error.response?.data?.message || 'Error fetching users. Please check server connection.', 
        'error'
      );
    } finally {
      setLoading(false);
      isRequestInProgressRef.current = false;
    }
  }, [actions]);

  // Create a stable reference to fetchUsers that doesn't change
  const fetchUsersRef = useRef();
  fetchUsersRef.current = fetchUsers;

  // Initial load
  useEffect(() => {
    fetchUsersRef.current(1, '', '');
    
  }, []); // Run only once on mount

  // Handle search execution
  const handleSearch = () => {
    if (!isRequestInProgressRef.current) {
      // Clear cursors when searching
      cursorsRef.current = {};
      setCurrentPage(1);
      setActiveSearch(searchTerm.trim());
      fetchUsersRef.current(1, searchTerm.trim(), '');
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearch('');
    cursorsRef.current = {};
    setCurrentPage(1);
    fetchUsersRef.current(1, '', '');
  };

  // Handle Enter key press in search field
  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };



  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    // Use stored cursor for this page if available
    const cursor = cursorsRef.current[page] || '';
    fetchUsersRef.current(page, searchTerm, cursor, true);
  };



  const handleNextPage = () => {
    if (hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchUsersRef.current(nextPage, activeSearch, nextCursor, true);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      // For previous page, we don't use cursor (start from beginning and paginate)
      fetchUsersRef.current(prevPage, activeSearch, '', true);
    }
  };

  const handleRefresh = () => {
    // Clear cursors and start fresh
    cursorsRef.current = {};
    setCurrentPage(1);
    fetchUsersRef.current(1, activeSearch, '', true);
  };



  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle user export
  const handleExportUsers = async () => {
    try {
      setExporting(true);
      
      // Validate dates
      if (!startDate || !endDate) {
        actions.showSnackbar('Please select both start and end dates', 'error');
        return;
      }
      
      if (new Date(startDate) > new Date(endDate)) {
        actions.showSnackbar('Start date cannot be after end date', 'error');
        return;
      }

      const diffDays = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
      if (diffDays > 15) {
        actions.showSnackbar('Date range cannot exceed 15 days', 'error');
        return;
      }

      // Build query parameters
      const params = {
        startDate: startDate,
        endDate: endDate,
        format: 'excel'
      };

      console.log('Exporting users with params:', params);

      // Make API call to download Excel
      const response = await api.get('/reports/users/export', {
        params,
        responseType: 'blob' // Important for file download
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      const filename = `users_export_${startDate}_to_${endDate}.xlsx`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      actions.showSnackbar('Users export downloaded successfully!', 'success');

    } catch (error) {
      console.error('Error exporting users:', error);
      actions.showSnackbar(
        error.response?.data?.message || 'Failed to export users',
        'error'
      );
    } finally {
      setExporting(false);
    }
  };



  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              User Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              component={Link}
              to="/super-admin/users/add"
              variant="contained"
              startIcon={<Add />}
            >
              Add New User
            </Button>
          </Box>
        </Box>

        {/* Export Section */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Export User Signup Data
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: startDate || undefined,
                max: startDate
                  ? new Date(new Date(startDate).getTime() + 15 * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .split('T')[0]
                  : undefined,
              }}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleExportUsers}
              disabled={exporting || !startDate || !endDate}
              sx={{ minWidth: 180, height: 40 }}
            >
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Export users who signed up between the selected dates (max 15-day range).
          </Typography>
        </Paper>

        {/* Search */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search users by name or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300, flexGrow: 1 }}
            disabled={loading}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || !searchTerm.trim()}
            sx={{ minWidth: 100 }}
          >
            Search
          </Button>
          {activeSearch && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              disabled={loading}
              sx={{ minWidth: 100 }}
            >
              Clear
            </Button>
          )}
        </Box>

        {/* User Count and Loading */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {users.length} users (Page {currentPage})
            {activeSearch && (
              <span style={{ fontWeight: 'bold' }}> - Search: "{activeSearch}"</span>
            )}
          </Typography>
          {loading && <CircularProgress size={20} />}
        </Box>

        {/* Users Table */}
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                minHeight: 200
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Mobile</strong></TableCell>
                  <TableCell><strong>Created On</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" component="div" color="text.secondary">
                      {loading ? 'Loading users...' : (activeSearch ? 'No users found matching your search.' : 'No users available.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell>
                      <Typography variant="body2" component="span" fontWeight="bold">
                        {user.f_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="span">
                        {user.mobileNo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="span">
                        {formatDate(user.timeStamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          color="primary"
                          component={Link}
                          to={`/super-admin/users/view/${user.userId}`}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>

        {/* Pagination */}
        {(hasNextPage || hasPrevPage) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handlePrevPage}
              disabled={!hasPrevPage || loading}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              Page {currentPage}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default UserList; 